import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/service';
import Spinner from "./Spinner";
import dashStyle from './Dashboard.module.css';
import './shared-content-styles.css';
import AuthContext from '../context/AuthContext';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [activeCategory, setActiveCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryLessons, setCategoryLessons] = useState({}); // Store lessons by category ID
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingLessons, setLoadingLessons] = useState({}); // Track loading state per category
  const [previewContent, setPreviewContent] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentLessonId, setCurrentLessonId] = useState(null);
  const [currentLessonTitle, setCurrentLessonTitle] = useState('');
  const [currentLessonCategory, setCurrentLessonCategory] = useState('');
  const [allLessons, setAllLessons] = useState([]);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(-1);

  const config = {
    headers: {
      Authorization: `Bearer ${user.token}`,
    },
  };

  // Cache configuration
  const CATEGORIES_CACHE_KEY = 'dashboard_categories';
  const LESSONS_CACHE_PREFIX = 'category_lessons_';
  const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

  // Cache utility functions
  const getCachedData = (key) => {
    try {
      const cached = localStorage.getItem(key);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = new Date().getTime();
        
        if (now - timestamp < CACHE_DURATION) {
          return data;
        } else {
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error('Error reading cache:', error);
      localStorage.removeItem(key);
    }
    return null;
  };

  const setCachedData = (key, data) => {
    try {
      const cacheObject = {
        data,
        timestamp: new Date().getTime()
      };
      localStorage.setItem(key, JSON.stringify(cacheObject));
    } catch (error) {
      console.error('Error setting cache:', error);
    }
  };

  const clearCache = () => {
    // Clear all related cache entries
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key === CATEGORIES_CACHE_KEY || key.startsWith(LESSONS_CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  };

  // Function to adjust content and remove checkboxes
  const adjustImagePaths = (content) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    
    doc.querySelectorAll('.list-bullet-todo-container').forEach(container => {
      container.remove();
    });
    
    doc.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.remove();
    });
    
    doc.querySelectorAll('li').forEach(li => {
      li.style.listStyleType = 'disc';
    });
    
    return doc.body.innerHTML;
  };

  // Load categories (without lessons)
  useEffect(() => {
    if (user.role === 'subscriber') {
      const fetchCategories = async () => {
        setLoadingCategories(true);
        
        // Try cache first
        const cachedCategories = getCachedData(CATEGORIES_CACHE_KEY);
        
        if (cachedCategories) {
          // Ensure cached data is an array
          const categoriesArray = Array.isArray(cachedCategories) ? cachedCategories : [];
          setCategories(categoriesArray);
          setLoadingCategories(false);
          
          // Optionally fetch fresh data in background
          try {
            const response = await axios.get('/lessons/categories-only?style=dashboard', config);
            if (Array.isArray(response.data) && JSON.stringify(response.data) !== JSON.stringify(categoriesArray)) {
              setCategories(response.data);
              setCachedData(CATEGORIES_CACHE_KEY, response.data);
            }
          } catch (error) {
            console.error('Background categories fetch failed:', error);
          }
        } else {
          // Fetch from server
          try {
            const response = await axios.get('/lessons/categories-only?style=dashboard', config);
            const categoriesData = Array.isArray(response.data) ? response.data : [];
            setCategories(categoriesData);
            setCachedData(CATEGORIES_CACHE_KEY, categoriesData);
          } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([]);
          } finally {
            setLoadingCategories(false);
          }
        }
      };
      
      fetchCategories();
    }
  }, [user]);

  // Load lessons for a specific category
  const loadCategoryLessons = async (categoryId, categoryName) => {
    // Check if already loaded
    if (categoryLessons[categoryId]) {
      return;
    }

    const cacheKey = `${LESSONS_CACHE_PREFIX}${categoryId}`;
    
    // Check cache first
    const cachedLessons = getCachedData(cacheKey);
    
    if (cachedLessons) {
      setCategoryLessons(prev => ({
        ...prev,
        [categoryId]: cachedLessons
      }));
      return;
    }

    // Set loading state
    setLoadingLessons(prev => ({ ...prev, [categoryId]: true }));

    try {
      const response = await axios.get(`/lessons/category/${categoryId}`, config);
      const lessons = response.data;
      
      setCategoryLessons(prev => ({
        ...prev,
        [categoryId]: lessons
      }));
      
      // Cache the lessons
      setCachedData(cacheKey, lessons);
      
    } catch (error) {
      console.error(`Error fetching lessons for category ${categoryName}:`, error);
    } finally {
      setLoadingLessons(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  // Build flat lessons array when needed (for navigation)
  useEffect(() => {
    const flatLessons = [];
    
    // Ensure categories is an array before iterating
    if (Array.isArray(categories)) {
      categories.forEach(category => {
        const lessons = categoryLessons[category._id];
        if (lessons && Array.isArray(lessons)) {
          lessons.forEach(lesson => {
            flatLessons.push({
              ...lesson,
              categoryName: category.title
            });
          });
        }
      });
    }
    
    setAllLessons(flatLessons);
  }, [categories, categoryLessons]);

  // Protection functions
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isPreviewOpen) {
        if (e.key === 'ArrowLeft' && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          navigateToPrevious();
          return;
        }
        
        if (e.key === 'ArrowRight' && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          navigateToNext();
          return;
        }
        
        if (e.key === 'Escape') {
          e.preventDefault();
          closePreview();
          return;
        }
        
        // Prevent various shortcuts
        if ((e.ctrlKey || e.metaKey) && ['p', 's', 'c', 'a', 'x', 'u'].includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        
        if (e.key === 'F12' || ((e.ctrlKey || e.metaKey) && e.shiftKey && ['I', 'J'].includes(e.key))) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }
    };

    const handleContextMenu = (e) => {
      if (isPreviewOpen) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const handleSelectStart = (e) => {
      if (isPreviewOpen) {
        e.preventDefault();
        return false;
      }
    };

    const handleDragStart = (e) => {
      if (isPreviewOpen) {
        e.preventDefault();
        return false;
      }
    };

    const originalPrint = window.print;
    if (isPreviewOpen) {
      window.print = () => console.log('Printing disabled');
    } else {
      window.print = originalPrint;
    }

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('contextmenu', handleContextMenu, true);
    document.addEventListener('selectstart', handleSelectStart, true);
    document.addEventListener('dragstart', handleDragStart, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('contextmenu', handleContextMenu, true);
      document.removeEventListener('selectstart', handleSelectStart, true);
      document.removeEventListener('dragstart', handleDragStart, true);
      window.print = originalPrint;
    };
  }, [isPreviewOpen, currentLessonIndex, allLessons]);

  const toggleCategory = async (category) => {
    const isOpening = activeCategory !== category.title;
    setActiveCategory(activeCategory === category.title ? null : category.title);
    
    // Load lessons if opening category and not already loaded
    if (isOpening && !categoryLessons[category._id]) {
      await loadCategoryLessons(category._id, category.title);
    }
  };

  const openPreview = async (lessonId, lessonTitle = '', categoryName = '') => {
    console.log('Opening preview with:', { lessonId, lessonTitle, categoryName });
    try {
      const response = await axios.get(`/lessons/${lessonId}`, config);
      const adjustedContent = adjustImagePaths(response.data);
      setPreviewContent(adjustedContent);
      setCurrentLessonId(lessonId);
      setCurrentLessonTitle(lessonTitle);
      setCurrentLessonCategory(categoryName);
      setIsPreviewOpen(true);
      
      const lessonIndex = allLessons.findIndex(lesson => lesson._id === lessonId);
      setCurrentLessonIndex(lessonIndex);
    } catch (error) {
      console.error("Error loading preview:", error);
    }
  };

  const navigateToPrevious = () => {
    if (currentLessonIndex > 0) {
      const previousLesson = allLessons[currentLessonIndex - 1];
      openPreview(previousLesson._id, previousLesson.title, previousLesson.categoryName);
    }
  };

  const navigateToNext = () => {
    if (currentLessonIndex < allLessons.length - 1) {
      const nextLesson = allLessons[currentLessonIndex + 1];
      openPreview(nextLesson._id, nextLesson.title, nextLesson.categoryName);
    }
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setPreviewContent('');
    setCurrentLessonId(null);
    setCurrentLessonTitle('');
    setCurrentLessonCategory('');
    setCurrentLessonIndex(-1);
  };

  const refreshCache = async () => {
    clearCache();
    setCategories([]);
    setCategoryLessons({});
    setLoadingCategories(true);
    
    try {
      const response = await axios.get('/lessons/categories-only?style=dashboard', config);
      const categoriesData = Array.isArray(response.data) ? response.data : [];
      setCategories(categoriesData);
      setCachedData(CATEGORIES_CACHE_KEY, categoriesData);
    } catch (error) {
      console.error('Error refreshing categories:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  if (user) {
    if (user.role === 'subscriber') {
      return (
        <div className={dashStyle.dashboardContainer}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1>Categories</h1>
            
          </div>
          
          {loadingCategories ? (
            <Spinner />
          ) : categories.length > 0 ? (
            <div className={dashStyle.colContainer}>
              {[0, 1, 2].map(colIndex => (
                <div key={colIndex} className={dashStyle.col}>
                  {Array.isArray(categories) && categories
                    .filter((_, index) => index % 3 === colIndex)
                    .map((category, index) => (
                      <div key={category._id} className={dashStyle.accordionItem}>
                        <div
                          className={dashStyle.accordionHeader}
                          onClick={() => toggleCategory(category)}
                          aria-expanded={activeCategory === category.title}
                        >
                          <span>{category.title}</span>
                          <span className={`${dashStyle.arrow} fa-solid fa-chevron-down`}></span>
                        </div>
                        
                        {activeCategory === category.title && (
                          <div className={dashStyle.accordionContent}>
                            {loadingLessons[category._id] ? (
                              <div style={{ padding: '20px', textAlign: 'center' }}>
                                <div style={{ 
                                  width: '20px', 
                                  height: '20px', 
                                  border: '2px solid #f3f3f3',
                                  borderTop: '2px solid #3498db',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite',
                                  margin: '0 auto'
                                }}></div>
                                <p style={{ marginTop: '10px', fontSize: '14px' }}>Loading lessons...</p>
                              </div>
                            ) : categoryLessons[category._id] && Array.isArray(categoryLessons[category._id]) ? (
                              <ul className={dashStyle.lessonList}>
                                {categoryLessons[category._id].map((lesson, lessonIndex) => (
                                  <li key={lesson._id}>
  <span
    className={dashStyle.lessonLink}
    onClick={() => {
      console.log('=== LESSON CLICK DEBUG ===');
      console.log('Category title from parent scope:', category.title);
      console.log('Full category object:', category);
      console.log('Lesson:', lesson);
      console.log('========================');
      openPreview(lesson._id, lesson.title, category.title);
    }}
    style={{ cursor: 'pointer' }}
  >
    {lesson.title}
  </span>
  <a
    href={`/lessons/${lesson._id}`}
    className={dashStyle.previewButton}
    aria-label="Go to lesson"
    target="_blank"
    rel="noopener noreferrer"
  >
    <i className="fas fa-arrow-up-right-from-square"></i>
  </a>
</li>

                                ))}
                              </ul>
                            ) : (
                              <p style={{ padding: '20px', textAlign: 'center' }}>No lessons found</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              ))}
            </div>
          ) : (
            <div>No categories found</div>
          )}

          {isPreviewOpen && (
            <div className={dashStyle.modalOverlay} onClick={closePreview}>
              <div 
                className={`${dashStyle.modalContent} ${dashStyle.protectedContent} lesson-content`} 
                onClick={(e) => e.stopPropagation()}
                onContextMenu={(e) => e.preventDefault()}
                onSelectStart={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
                style={{
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                  WebkitUserDrag: 'none',
                  KhtmlUserSelect: 'none'
                }}
              >
                <div className={dashStyle.modalHeader}>
                  <div className={dashStyle.navigationControls}>
                    <button 
                      className={`${dashStyle.navButton} ${currentLessonIndex === 0 ? dashStyle.disabled : ''}`}
                      onClick={navigateToPrevious}
                      disabled={currentLessonIndex === 0}
                      title="Previous lesson (←)"
                    >
                      <i className="fas fa-chevron-left"></i>
                      Previous
                    </button>
                    
                    <div className={dashStyle.lessonInfo}>
                      <h3 className={dashStyle.modalTitle}>{currentLessonTitle}</h3>
                      {currentLessonCategory && (
                        <span className={dashStyle.categoryBadge}>{currentLessonCategory}</span>
                      )}
                    </div>
                    
                    <button 
                      className={`${dashStyle.navButton} ${currentLessonIndex === allLessons.length - 1 ? dashStyle.disabled : ''}`}
                      onClick={navigateToNext}
                      disabled={currentLessonIndex === allLessons.length - 1}
                      title="Next lesson (→)"
                    >
                      Next
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                  
                  <div className={dashStyle.modalControls}>
                    <button className={dashStyle.closeButton} onClick={closePreview}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>

                <div 
                  className={dashStyle.modalBody}
                  dangerouslySetInnerHTML={{ __html: previewContent }}
                  style={{
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitUserDrag: 'none',
                    KhtmlUserSelect: 'none',
                    pointerEvents: 'auto'
                  }}
                />
              </div>
            </div>
          )}
        </div>
      );
    } else if (user.role === 'user') {
      return (
        <div className={dashStyle.dashboardContainer}>
          <h1>Subscribe to Get Access</h1>
          <p>
            If you wish to get access to the content, please <Link to={"/subscribe"}>subscribe</Link>.
          </p>
        </div>
      );
    } else {
      return (
        <div className={dashStyle.dashboardContainer}>
          <h1>Welcome Admin</h1>
          <p>See <a href="/lessons">Lessons</a> page</p>
          <p>or edit <a href="/admin/lessons">Lessons</a></p>
        </div>
      );
    }
  } else {
    return (
      <div className={dashStyle.dashboardContainer}>
        <p>You're not authorized to see this content.</p>
        <p>Please, log in.</p>
      </div>
    );
  }
};

export default Dashboard;